/**
 * Industry Configuration - Source of Truth
 * 
 * This file contains all industry definitions, terminology, and API mappings.
 * Used by CorvanaAI to understand industry-specific language and data sources.
 * 
 * Structure:
 * - INDUSTRY_CATEGORIES: Main categories with sub-industries
 * - INDUSTRY_TERMINOLOGY: Industry-specific language (customers, appointments, etc.)
 * - INDUSTRY_API_MAPPINGS: Relevant API integrations per industry
 * - Helper functions for lookups
 */

import { 
  Stethoscope, UtensilsCrossed, ShoppingCart, Briefcase, Code2, 
  HardHat, GraduationCap, PiggyBank, Building, Dumbbell, Car,
  Scissors, Truck, Factory, Leaf, Plane, Music, Heart, Home,
  Camera, Palette, Shield, Baby, Dog, Anchor, Zap, Wrench,
  BookOpen, Scale, Gavel, Calculator, Users, Sparkles, Coffee
} from 'lucide-react';

// =============================================================================
// INDUSTRY CATEGORIES - Comprehensive List
// =============================================================================
export const INDUSTRY_CATEGORIES = [
  // -------------------------------------------------------------------------
  // HEALTHCARE & MEDICAL (ANZSIC Division Q)
  // -------------------------------------------------------------------------
  {
    id: 'healthcare',
    name: 'Healthcare & Medical',
    icon: 'Stethoscope',
    color: '#00A99D',
    description: 'Medical practices, clinics, and health services',
    anzsic_code: 'Q',
    anzsic_division: 'Health Care and Social Assistance',
    subIndustries: [
      { id: 'general_practice', name: 'General Practice / GP Clinic' },
      { id: 'dental', name: 'Dental & Orthodontics' },
      { id: 'physio', name: 'Physiotherapy' },
      { id: 'chiropractic', name: 'Chiropractic' },
      { id: 'osteopathy', name: 'Osteopathy' },
      { id: 'podiatry', name: 'Podiatry' },
      { id: 'optometry', name: 'Optometry & Eye Care' },
      { id: 'audiology', name: 'Audiology & Hearing' },
      { id: 'psychology', name: 'Psychology & Counselling' },
      { id: 'psychiatry', name: 'Psychiatry' },
      { id: 'speech_pathology', name: 'Speech Pathology' },
      { id: 'occupational_therapy', name: 'Occupational Therapy' },
      { id: 'dietitian', name: 'Dietitian & Nutrition' },
      { id: 'naturopathy', name: 'Naturopathy & Holistic Health' },
      { id: 'acupuncture', name: 'Acupuncture & TCM' },
      { id: 'massage_therapy', name: 'Remedial Massage Therapy' },
      { id: 'pharmacy', name: 'Pharmacy & Chemist' },
      { id: 'pathology', name: 'Pathology Lab' },
      { id: 'radiology', name: 'Radiology & Imaging' },
      { id: 'specialist_clinic', name: 'Medical Specialist Clinic' },
      { id: 'cosmetic_clinic', name: 'Cosmetic & Aesthetic Clinic' },
      { id: 'ivf_fertility', name: 'IVF & Fertility Clinic' },
      { id: 'allied_health', name: 'Allied Health Centre' },
      { id: 'home_care', name: 'Home Care Services' },
      { id: 'aged_care', name: 'Aged Care & Nursing Home' },
      { id: 'ndis_provider', name: 'NDIS Service Provider' },
      { id: 'mental_health', name: 'Mental Health Services' },
      { id: 'rehabilitation', name: 'Rehabilitation Centre' },
      { id: 'sleep_clinic', name: 'Sleep Clinic' },
      { id: 'urgent_care', name: 'Urgent Care / Walk-in Clinic' },
    ]
  },
  
  // -------------------------------------------------------------------------
  // VETERINARY & ANIMAL CARE (ANZSIC Division Q/S)
  // -------------------------------------------------------------------------
  {
    id: 'veterinary',
    name: 'Veterinary & Animal Care',
    icon: 'Dog',
    color: '#10B981',
    description: 'Animal health and pet services',
    anzsic_code: 'Q',
    anzsic_division: 'Health Care and Social Assistance',
    subIndustries: [
      { id: 'vet_clinic', name: 'Veterinary Clinic' },
      { id: 'vet_hospital', name: 'Veterinary Hospital' },
      { id: 'emergency_vet', name: 'Emergency Vet Services' },
      { id: 'mobile_vet', name: 'Mobile Veterinary' },
      { id: 'equine_vet', name: 'Equine Veterinary' },
      { id: 'exotic_vet', name: 'Exotic Animal Vet' },
      { id: 'pet_grooming', name: 'Pet Grooming' },
      { id: 'pet_boarding', name: 'Pet Boarding & Kennels' },
      { id: 'dog_daycare', name: 'Dog Daycare' },
      { id: 'dog_training', name: 'Dog Training' },
      { id: 'pet_sitting', name: 'Pet Sitting & Walking' },
      { id: 'pet_store', name: 'Pet Store & Supplies' },
      { id: 'aquarium_services', name: 'Aquarium Services' },
      { id: 'animal_rescue', name: 'Animal Rescue & Shelter' },
    ]
  },

  // -------------------------------------------------------------------------
  // HOSPITALITY & FOOD SERVICE (ANZSIC Division H)
  // -------------------------------------------------------------------------
  {
    id: 'hospitality',
    name: 'Hospitality & Food Service',
    icon: 'UtensilsCrossed',
    color: '#FF6B35',
    description: 'Restaurants, cafes, bars, and food businesses',
    anzsic_code: 'H',
    anzsic_division: 'Accommodation and Food Services',
    subIndustries: [
      // Subdivision 44: Accommodation
      { id: 'hotel', name: 'Hotel' },
      { id: 'motel', name: 'Motel' },
      { id: 'serviced_apartments', name: 'Serviced Apartments' },
      { id: 'caravan_park', name: 'Caravan Park & Camping' },
      // Subdivision 45: Food and Beverage Services
      { id: 'cafe', name: 'Café & Coffee Shop' },
      { id: 'restaurant', name: 'Restaurant (Full Service)' },
      { id: 'fast_food', name: 'Takeaway & Fast Food' },
      { id: 'catering', name: 'Catering Services' },
      { id: 'bar', name: 'Bar & Pub' },
      { id: 'club', name: 'Club (Licensed)' },
      { id: 'bakery', name: 'Bakery' },
      // Additional common subcategories
      { id: 'food_truck', name: 'Food Truck & Mobile' },
      { id: 'function_venue', name: 'Function & Event Venue' },
    ]
  },

  // -------------------------------------------------------------------------
  // ACCOMMODATION & TOURISM (ANZSIC Division H & R)
  // -------------------------------------------------------------------------
  {
    id: 'accommodation',
    name: 'Accommodation & Tourism',
    icon: 'Building',
    color: '#0EA5E9',
    description: 'Hotels, motels, and travel services',
    anzsic_code: 'H',
    anzsic_division: 'Accommodation and Food Services',
    subIndustries: [
      { id: 'hotel', name: 'Hotel' },
      { id: 'motel', name: 'Motel' },
      { id: 'resort', name: 'Resort' },
      { id: 'serviced_apartments', name: 'Serviced Apartments' },
      { id: 'bnb', name: 'Bed & Breakfast' },
      { id: 'holiday_rental', name: 'Holiday Rental / Short-Stay' },
      { id: 'caravan_park', name: 'Caravan Park & Camping' },
      { id: 'tour_operator', name: 'Tour Operator' },
      { id: 'travel_agency', name: 'Travel Agency' },
    ]
  },

  // -------------------------------------------------------------------------
  // RETAIL & E-COMMERCE (ANZSIC Division G)
  // -------------------------------------------------------------------------
  {
    id: 'retail',
    name: 'Retail & E-commerce',
    icon: 'ShoppingCart',
    color: '#7AB55C',
    description: 'Stores, shops, and online retail',
    anzsic_code: 'G',
    anzsic_division: 'Retail Trade',
    subIndustries: [
      { id: 'fashion_retail', name: 'Fashion & Apparel' },
      { id: 'footwear', name: 'Footwear & Shoes' },
      { id: 'accessories', name: 'Accessories & Bags' },
      { id: 'jewellery', name: 'Jewellery Store' },
      { id: 'watches', name: 'Watch Store' },
      { id: 'beauty_retail', name: 'Beauty & Cosmetics Retail' },
      { id: 'skincare', name: 'Skincare Store' },
      { id: 'fragrance', name: 'Fragrance & Perfume' },
      { id: 'electronics', name: 'Electronics & Tech Store' },
      { id: 'appliances', name: 'Appliances & Whitegoods' },
      { id: 'furniture', name: 'Furniture Store' },
      { id: 'homewares', name: 'Homewares & Decor' },
      { id: 'bedding', name: 'Bedding & Manchester' },
      { id: 'lighting', name: 'Lighting Store' },
      { id: 'grocery', name: 'Grocery & Supermarket' },
      { id: 'convenience', name: 'Convenience Store' },
      { id: 'liquor', name: 'Liquor Store / Bottle Shop' },
      { id: 'tobacconist', name: 'Tobacconist' },
      { id: 'newsagent', name: 'Newsagent' },
      { id: 'bookstore', name: 'Bookstore' },
      { id: 'stationery', name: 'Stationery & Office Supplies' },
      { id: 'toys', name: 'Toys & Games' },
      { id: 'baby_kids', name: 'Baby & Kids Store' },
      { id: 'sporting_goods', name: 'Sporting Goods' },
      { id: 'outdoor_camping', name: 'Outdoor & Camping' },
      { id: 'cycling', name: 'Cycling & Bike Shop' },
      { id: 'fishing_tackle', name: 'Fishing & Tackle' },
      { id: 'gun_shop', name: 'Gun Shop & Firearms' },
      { id: 'music_instruments', name: 'Musical Instruments' },
      { id: 'art_supplies', name: 'Art Supplies' },
      { id: 'craft_hobby', name: 'Craft & Hobby Store' },
      { id: 'florist', name: 'Florist' },
      { id: 'gift_shop', name: 'Gift Shop' },
      { id: 'second_hand', name: 'Second Hand / Op Shop' },
      { id: 'antiques', name: 'Antiques & Collectibles' },
      { id: 'pawn_shop', name: 'Pawn Shop' },
      { id: 'vape_shop', name: 'Vape Shop' },
      { id: 'health_food', name: 'Health Food Store' },
      { id: 'supplement', name: 'Supplement & Nutrition Store' },
      { id: 'ecommerce_general', name: 'E-commerce Store (General)' },
      { id: 'dropshipping', name: 'Dropshipping Business' },
      { id: 'marketplace_seller', name: 'Marketplace Seller (Amazon, eBay)' },
    ]
  },

  // -------------------------------------------------------------------------
  // AUTOMOTIVE (ANZSIC Division F/G)
  // -------------------------------------------------------------------------
  {
    id: 'automotive',
    name: 'Automotive',
    icon: 'Car',
    color: '#EF4444',
    description: 'Car sales, service, and related businesses',
    anzsic_code: 'G',
    anzsic_division: 'Retail Trade / Motor Vehicle Services',
    subIndustries: [
      { id: 'car_dealership', name: 'Car Dealership (New)' },
      { id: 'used_car', name: 'Used Car Dealer' },
      { id: 'motorcycle_dealer', name: 'Motorcycle Dealer' },
      { id: 'caravan_dealer', name: 'Caravan & RV Dealer' },
      { id: 'boat_dealer', name: 'Boat & Marine Dealer' },
      { id: 'auto_mechanic', name: 'Auto Mechanic / Workshop' },
      { id: 'auto_electrical', name: 'Auto Electrical' },
      { id: 'smash_repair', name: 'Smash Repair & Panel Beater' },
      { id: 'windscreen', name: 'Windscreen Repair' },
      { id: 'tyre_shop', name: 'Tyre Shop' },
      { id: 'car_wash', name: 'Car Wash' },
      { id: 'detailing', name: 'Car Detailing' },
      { id: 'car_rental', name: 'Car Rental' },
      { id: 'truck_rental', name: 'Truck & Van Rental' },
      { id: 'towing', name: 'Towing Service' },
      { id: 'roadside_assist', name: 'Roadside Assistance' },
      { id: 'auto_parts', name: 'Auto Parts Retail' },
      { id: 'performance_parts', name: 'Performance Parts' },
      { id: 'auto_accessories', name: 'Auto Accessories' },
      { id: 'audio_install', name: 'Car Audio Installation' },
      { id: 'fleet_management', name: 'Fleet Management' },
      { id: 'driving_school', name: 'Driving School' },
    ]
  },

  // -------------------------------------------------------------------------
  // BEAUTY & PERSONAL CARE (ANZSIC Division S)
  // -------------------------------------------------------------------------
  {
    id: 'beauty',
    name: 'Beauty & Personal Care',
    icon: 'Scissors',
    color: '#EC4899',
    description: 'Salons, spas, and beauty services',
    anzsic_code: 'S',
    anzsic_division: 'Other Services',
    subIndustries: [
      { id: 'hair_salon', name: 'Hair Salon' },
      { id: 'barbershop', name: 'Barbershop' },
      { id: 'beauty_salon', name: 'Beauty Salon' },
      { id: 'nail_salon', name: 'Nail Salon' },
      { id: 'day_spa', name: 'Day Spa' },
      { id: 'med_spa', name: 'Medical Spa / Medi Spa' },
      { id: 'massage_spa', name: 'Massage & Relaxation Spa' },
      { id: 'waxing', name: 'Waxing Studio' },
      { id: 'laser_clinic', name: 'Laser Clinic' },
      { id: 'skin_clinic', name: 'Skin Clinic' },
      { id: 'eyebrow_lash', name: 'Eyebrow & Lash Studio' },
      { id: 'tanning', name: 'Tanning Salon' },
      { id: 'tattoo', name: 'Tattoo Studio' },
      { id: 'piercing', name: 'Piercing Studio' },
      { id: 'cosmetic_tattooing', name: 'Cosmetic Tattooing' },
      { id: 'mobile_beauty', name: 'Mobile Beauty Services' },
      { id: 'bridal_beauty', name: 'Bridal Beauty' },
      { id: 'makeup_artist', name: 'Makeup Artist' },
    ]
  },

  // -------------------------------------------------------------------------
  // FITNESS & WELLNESS (ANZSIC Division R)
  // -------------------------------------------------------------------------
  {
    id: 'fitness',
    name: 'Fitness & Wellness',
    icon: 'Dumbbell',
    color: '#F97316',
    description: 'Gyms, studios, and wellness services',
    anzsic_code: 'R',
    anzsic_division: 'Arts and Recreation Services',
    subIndustries: [
      { id: 'gym', name: 'Gym & Fitness Centre' },
      { id: 'crossfit', name: 'CrossFit Box' },
      { id: 'boutique_fitness', name: 'Boutique Fitness Studio' },
      { id: 'yoga_studio', name: 'Yoga Studio' },
      { id: 'pilates', name: 'Pilates Studio' },
      { id: 'spin_cycling', name: 'Spin / Indoor Cycling' },
      { id: 'boxing_gym', name: 'Boxing Gym' },
      { id: 'martial_arts', name: 'Martial Arts Studio' },
      { id: 'dance_studio', name: 'Dance Studio' },
      { id: 'personal_training', name: 'Personal Training Studio' },
      { id: 'mobile_pt', name: 'Mobile Personal Trainer' },
      { id: 'swim_school', name: 'Swim School' },
      { id: 'aquatic_centre', name: 'Aquatic Centre' },
      { id: 'tennis_centre', name: 'Tennis Centre' },
      { id: 'golf_club', name: 'Golf Club' },
      { id: 'climbing_gym', name: 'Climbing Gym' },
      { id: 'trampoline_park', name: 'Trampoline Park' },
      { id: 'sports_club', name: 'Sports Club' },
      { id: 'health_retreat', name: 'Health Retreat' },
      { id: 'wellness_centre', name: 'Wellness Centre' },
      { id: 'float_therapy', name: 'Float Therapy' },
      { id: 'cryotherapy', name: 'Cryotherapy' },
      { id: 'sauna_bathhouse', name: 'Sauna & Bathhouse' },
    ]
  },

  // -------------------------------------------------------------------------
  // CONSTRUCTION & TRADES (ANZSIC Division E)
  // -------------------------------------------------------------------------
  {
    id: 'construction',
    name: 'Construction & Trades',
    icon: 'HardHat',
    color: '#FF6600',
    description: 'Building, renovation, and trade services',
    anzsic_code: 'E',
    anzsic_division: 'Construction',
    subIndustries: [
      { id: 'builder_residential', name: 'Residential Builder' },
      { id: 'builder_commercial', name: 'Commercial Builder' },
      { id: 'renovation', name: 'Renovation & Extensions' },
      { id: 'granny_flat', name: 'Granny Flat Builder' },
      { id: 'project_home', name: 'Project Home Builder' },
      { id: 'custom_home', name: 'Custom Home Builder' },
      { id: 'electrician', name: 'Electrician' },
      { id: 'plumber', name: 'Plumber' },
      { id: 'gas_fitter', name: 'Gas Fitter' },
      { id: 'hvac', name: 'HVAC & Air Conditioning' },
      { id: 'refrigeration', name: 'Refrigeration' },
      { id: 'roofing', name: 'Roofing' },
      { id: 'guttering', name: 'Guttering & Downpipes' },
      { id: 'carpentry', name: 'Carpentry' },
      { id: 'joinery', name: 'Joinery & Cabinet Making' },
      { id: 'tiling', name: 'Tiling' },
      { id: 'plastering', name: 'Plastering & Gyprocking' },
      { id: 'painting', name: 'Painting & Decorating' },
      { id: 'flooring', name: 'Flooring Installation' },
      { id: 'concreting', name: 'Concreting' },
      { id: 'bricklaying', name: 'Bricklaying' },
      { id: 'rendering', name: 'Rendering' },
      { id: 'waterproofing', name: 'Waterproofing' },
      { id: 'demolition', name: 'Demolition' },
      { id: 'excavation', name: 'Excavation' },
      { id: 'earthmoving', name: 'Earthmoving' },
      { id: 'scaffolding', name: 'Scaffolding' },
      { id: 'crane_hire', name: 'Crane Hire' },
      { id: 'glazier', name: 'Glazier' },
      { id: 'pool_building', name: 'Pool Building' },
      { id: 'landscaping', name: 'Landscaping' },
      { id: 'fencing', name: 'Fencing' },
      { id: 'paving', name: 'Paving' },
      { id: 'deck_pergola', name: 'Deck & Pergola' },
      { id: 'garden_maintenance', name: 'Garden Maintenance' },
      { id: 'tree_services', name: 'Tree Services / Arborist' },
      { id: 'irrigation', name: 'Irrigation' },
      { id: 'solar_install', name: 'Solar Installation' },
      { id: 'security_install', name: 'Security System Installation' },
      { id: 'antenna_install', name: 'Antenna & TV Installation' },
      { id: 'handyman', name: 'Handyman Services' },
    ]
  },

  // -------------------------------------------------------------------------
  // PROFESSIONAL SERVICES (ANZSIC Division M)
  // -------------------------------------------------------------------------
  {
    id: 'professional',
    name: 'Professional Services',
    icon: 'Briefcase',
    color: '#6366f1',
    description: 'Business and professional service providers',
    anzsic_code: 'M',
    anzsic_division: 'Professional, Scientific and Technical Services',
    subIndustries: [
      { id: 'law_firm', name: 'Law Firm' },
      { id: 'barrister', name: 'Barrister Chambers' },
      { id: 'conveyancer', name: 'Conveyancer' },
      { id: 'accounting_firm', name: 'Accounting Firm' },
      { id: 'bookkeeper', name: 'Bookkeeper' },
      { id: 'tax_agent', name: 'Tax Agent' },
      { id: 'financial_planner', name: 'Financial Planner' },
      { id: 'mortgage_broker', name: 'Mortgage Broker' },
      { id: 'insurance_broker', name: 'Insurance Broker' },
      { id: 'real_estate', name: 'Real Estate Agency' },
      { id: 'property_management', name: 'Property Management' },
      { id: 'buyers_agent', name: 'Buyers Agent' },
      { id: 'valuer', name: 'Property Valuer' },
      { id: 'surveyor', name: 'Surveyor' },
      { id: 'architect', name: 'Architect' },
      { id: 'interior_designer', name: 'Interior Designer' },
      { id: 'engineering_firm', name: 'Engineering Firm' },
      { id: 'consulting', name: 'Management Consulting' },
      { id: 'hr_consulting', name: 'HR Consulting' },
      { id: 'it_consulting', name: 'IT Consulting' },
      { id: 'marketing_agency', name: 'Marketing Agency' },
      { id: 'pr_agency', name: 'PR Agency' },
      { id: 'advertising_agency', name: 'Advertising Agency' },
      { id: 'design_agency', name: 'Design Agency' },
      { id: 'recruitment', name: 'Recruitment Agency' },
      { id: 'labour_hire', name: 'Labour Hire' },
      { id: 'translation', name: 'Translation Services' },
      { id: 'patent_attorney', name: 'Patent Attorney' },
      { id: 'migration_agent', name: 'Migration Agent' },
      { id: 'private_investigator', name: 'Private Investigator' },
    ]
  },

  // -------------------------------------------------------------------------
  // TECHNOLOGY & DIGITAL (ANZSIC Division J)
  // -------------------------------------------------------------------------
  {
    id: 'technology',
    name: 'Technology & Digital',
    icon: 'Code2',
    color: '#635BFF',
    description: 'Tech companies, digital agencies, and IT services',
    anzsic_code: 'J',
    anzsic_division: 'Information Media and Telecommunications',
    subIndustries: [
      { id: 'software_company', name: 'Software Company' },
      { id: 'saas', name: 'SaaS Business' },
      { id: 'app_development', name: 'App Development' },
      { id: 'web_development', name: 'Web Development' },
      { id: 'it_support', name: 'IT Support & MSP' },
      { id: 'cybersecurity', name: 'Cybersecurity' },
      { id: 'data_analytics', name: 'Data Analytics' },
      { id: 'ai_ml', name: 'AI & Machine Learning' },
      { id: 'cloud_services', name: 'Cloud Services' },
      { id: 'digital_agency', name: 'Digital Agency' },
      { id: 'seo_agency', name: 'SEO Agency' },
      { id: 'social_media_agency', name: 'Social Media Agency' },
      { id: 'content_agency', name: 'Content Agency' },
      { id: 'video_production', name: 'Video Production' },
      { id: 'game_development', name: 'Game Development' },
      { id: 'tech_startup', name: 'Tech Startup' },
      { id: 'fintech', name: 'Fintech' },
      { id: 'edtech', name: 'EdTech' },
      { id: 'healthtech', name: 'HealthTech' },
      { id: 'proptech', name: 'PropTech' },
      { id: 'telecommunications', name: 'Telecommunications' },
      { id: 'isp', name: 'Internet Service Provider' },
      { id: 'computer_repairs', name: 'Computer Repairs' },
      { id: 'phone_repairs', name: 'Phone Repairs' },
    ]
  },

  // -------------------------------------------------------------------------
  // EDUCATION & TRAINING (ANZSIC Division P)
  // -------------------------------------------------------------------------
  {
    id: 'education',
    name: 'Education & Training',
    icon: 'GraduationCap',
    color: '#8B5CF6',
    description: 'Schools, training providers, and educational services',
    anzsic_code: 'P',
    anzsic_division: 'Education and Training',
    subIndustries: [
      { id: 'childcare', name: 'Childcare Centre' },
      { id: 'preschool', name: 'Preschool / Kindergarten' },
      { id: 'primary_school', name: 'Primary School' },
      { id: 'secondary_school', name: 'Secondary School' },
      { id: 'tutoring', name: 'Tutoring Centre' },
      { id: 'online_tutoring', name: 'Online Tutoring' },
      { id: 'language_school', name: 'Language School' },
      { id: 'music_school', name: 'Music School' },
      { id: 'art_school', name: 'Art School' },
      { id: 'drama_school', name: 'Drama & Acting School' },
      { id: 'driving_school_edu', name: 'Driving School' },
      { id: 'rto', name: 'RTO / Vocational Training' },
      { id: 'online_courses', name: 'Online Course Provider' },
      { id: 'corporate_training', name: 'Corporate Training' },
      { id: 'coaching', name: 'Coaching & Mentoring' },
      { id: 'test_prep', name: 'Test Prep Centre' },
      { id: 'study_abroad', name: 'Study Abroad Agency' },
      { id: 'oosh', name: 'OOSH / After School Care' },
      { id: 'vacation_care', name: 'Vacation Care' },
      { id: 'special_needs', name: 'Special Needs Education' },
    ]
  },

  // -------------------------------------------------------------------------
  // WHOLESALE & DISTRIBUTION (ANZSIC Division F)
  // -------------------------------------------------------------------------
  {
    id: 'wholesale',
    name: 'Wholesale & Distribution',
    icon: 'Truck',
    color: '#0891B2',
    description: 'Wholesalers, distributors, and B2B suppliers',
    anzsic_code: 'F',
    anzsic_division: 'Wholesale Trade',
    subIndustries: [
      { id: 'food_distributor', name: 'Food & Beverage Distributor' },
      { id: 'liquor_distributor', name: 'Liquor Distributor' },
      { id: 'fresh_produce', name: 'Fresh Produce Wholesale' },
      { id: 'meat_wholesale', name: 'Meat Wholesale' },
      { id: 'seafood_wholesale', name: 'Seafood Wholesale' },
      { id: 'bakery_wholesale', name: 'Bakery Wholesale' },
      { id: 'building_supplies', name: 'Building Supplies Wholesale' },
      { id: 'timber_wholesale', name: 'Timber Wholesale' },
      { id: 'steel_wholesale', name: 'Steel & Metals Wholesale' },
      { id: 'plumbing_wholesale', name: 'Plumbing Supplies Wholesale' },
      { id: 'electrical_wholesale', name: 'Electrical Supplies Wholesale' },
      { id: 'pool_spa_wholesale', name: 'Pool & Spa Wholesale' },
      { id: 'hvac_wholesale', name: 'HVAC Wholesale' },
      { id: 'automotive_wholesale', name: 'Automotive Parts Wholesale' },
      { id: 'industrial_supplies', name: 'Industrial Supplies' },
      { id: 'safety_supplies', name: 'Safety & PPE Wholesale' },
      { id: 'packaging_wholesale', name: 'Packaging Wholesale' },
      { id: 'cleaning_supplies', name: 'Cleaning Supplies Wholesale' },
      { id: 'office_supplies', name: 'Office Supplies Wholesale' },
      { id: 'furniture_wholesale', name: 'Furniture Wholesale' },
      { id: 'textile_wholesale', name: 'Textile Wholesale' },
      { id: 'cosmetic_wholesale', name: 'Cosmetic & Beauty Wholesale' },
      { id: 'pharmaceutical_dist', name: 'Pharmaceutical Distributor' },
      { id: 'medical_supplies', name: 'Medical Supplies Distributor' },
      { id: 'electronics_wholesale', name: 'Electronics Wholesale' },
      { id: 'toy_wholesale', name: 'Toy Wholesale' },
      { id: 'general_wholesale', name: 'General Wholesale' },
      { id: 'import_export', name: 'Import/Export Business' },
    ]
  },

  // -------------------------------------------------------------------------
  // MANUFACTURING & PRODUCTION (ANZSIC Division C)
  // -------------------------------------------------------------------------
  {
    id: 'manufacturing',
    name: 'Manufacturing & Production',
    icon: 'Factory',
    color: '#64748B',
    description: 'Manufacturers and production facilities',
    anzsic_code: 'C',
    anzsic_division: 'Manufacturing',
    subIndustries: [
      { id: 'food_manufacturing', name: 'Food Manufacturing' },
      { id: 'beverage_manufacturing', name: 'Beverage Manufacturing' },
      { id: 'bakery_manufacturing', name: 'Commercial Bakery' },
      { id: 'meat_processing', name: 'Meat Processing' },
      { id: 'dairy_manufacturing', name: 'Dairy Manufacturing' },
      { id: 'metal_fabrication', name: 'Metal Fabrication' },
      { id: 'steel_manufacturing', name: 'Steel Manufacturing' },
      { id: 'plastic_manufacturing', name: 'Plastic Manufacturing' },
      { id: 'rubber_manufacturing', name: 'Rubber Manufacturing' },
      { id: 'chemical_manufacturing', name: 'Chemical Manufacturing' },
      { id: 'pharmaceutical_mfg', name: 'Pharmaceutical Manufacturing' },
      { id: 'cosmetic_manufacturing', name: 'Cosmetic Manufacturing' },
      { id: 'textile_manufacturing', name: 'Textile Manufacturing' },
      { id: 'clothing_manufacturing', name: 'Clothing Manufacturing' },
      { id: 'furniture_manufacturing', name: 'Furniture Manufacturing' },
      { id: 'woodworking', name: 'Woodworking & Millwork' },
      { id: 'glass_manufacturing', name: 'Glass Manufacturing' },
      { id: 'concrete_products', name: 'Concrete Products' },
      { id: 'electronics_manufacturing', name: 'Electronics Manufacturing' },
      { id: 'machinery_manufacturing', name: 'Machinery Manufacturing' },
      { id: 'automotive_manufacturing', name: 'Automotive Manufacturing' },
      { id: 'aerospace_manufacturing', name: 'Aerospace Manufacturing' },
      { id: 'printing', name: 'Printing & Publishing' },
      { id: 'packaging_manufacturing', name: 'Packaging Manufacturing' },
      { id: 'signage_manufacturing', name: 'Signage Manufacturing' },
      { id: 'custom_manufacturing', name: 'Custom Manufacturing' },
    ]
  },

  // -------------------------------------------------------------------------
  // TRANSPORT & LOGISTICS (ANZSIC Division I)
  // -------------------------------------------------------------------------
  {
    id: 'transport',
    name: 'Transport & Logistics',
    icon: 'Truck',
    color: '#1E40AF',
    description: 'Transport, freight, and logistics services',
    anzsic_code: 'I',
    anzsic_division: 'Transport, Postal and Warehousing',
    subIndustries: [
      { id: 'courier', name: 'Courier Service' },
      { id: 'freight_forwarding', name: 'Freight Forwarding' },
      { id: 'trucking', name: 'Trucking Company' },
      { id: 'removalist', name: 'Removalist / Moving Company' },
      { id: 'storage', name: 'Storage & Warehousing' },
      { id: '3pl', name: '3PL Provider' },
      { id: 'last_mile', name: 'Last Mile Delivery' },
      { id: 'taxi', name: 'Taxi Company' },
      { id: 'rideshare', name: 'Rideshare Driver' },
      { id: 'limousine', name: 'Limousine & Chauffeur' },
      { id: 'bus_company', name: 'Bus Company' },
      { id: 'charter_bus', name: 'Charter Bus' },
      { id: 'airport_transfer', name: 'Airport Transfer' },
      { id: 'patient_transport', name: 'Patient Transport' },
      { id: 'school_bus', name: 'School Bus Service' },
      { id: 'shipping', name: 'Shipping Company' },
      { id: 'container_transport', name: 'Container Transport' },
      { id: 'cold_chain', name: 'Cold Chain Logistics' },
      { id: 'hazmat_transport', name: 'Hazmat Transport' },
    ]
  },

  // -------------------------------------------------------------------------
  // HOME SERVICES (ANZSIC Division S)
  // -------------------------------------------------------------------------
  {
    id: 'home_services',
    name: 'Home Services',
    icon: 'Home',
    color: '#059669',
    description: 'Residential cleaning, maintenance, and home services',
    anzsic_code: 'S',
    anzsic_division: 'Other Services',
    subIndustries: [
      { id: 'house_cleaning', name: 'House Cleaning' },
      { id: 'commercial_cleaning', name: 'Commercial Cleaning' },
      { id: 'carpet_cleaning', name: 'Carpet Cleaning' },
      { id: 'window_cleaning', name: 'Window Cleaning' },
      { id: 'pressure_washing', name: 'Pressure Washing' },
      { id: 'end_of_lease', name: 'End of Lease Cleaning' },
      { id: 'ndis_cleaning', name: 'NDIS Cleaning Services' },
      { id: 'pest_control', name: 'Pest Control' },
      { id: 'termite_control', name: 'Termite Control' },
      { id: 'locksmith', name: 'Locksmith' },
      { id: 'appliance_repair', name: 'Appliance Repair' },
      { id: 'pool_service', name: 'Pool Service & Maintenance' },
      { id: 'garage_door', name: 'Garage Door Service' },
      { id: 'home_security', name: 'Home Security' },
      { id: 'home_organiser', name: 'Professional Organiser' },
      { id: 'laundry_service', name: 'Laundry & Dry Cleaning' },
      { id: 'waste_collection', name: 'Waste Collection' },
      { id: 'skip_bin', name: 'Skip Bin Hire' },
      { id: 'rubbish_removal', name: 'Rubbish Removal' },
    ]
  },

  // -------------------------------------------------------------------------
  // EVENTS & ENTERTAINMENT (ANZSIC Division R)
  // -------------------------------------------------------------------------
  {
    id: 'events',
    name: 'Events & Entertainment',
    icon: 'Music',
    color: '#DB2777',
    description: 'Event planning, entertainment, and creative services',
    anzsic_code: 'R',
    anzsic_division: 'Arts and Recreation Services',
    subIndustries: [
      { id: 'event_planner', name: 'Event Planner' },
      { id: 'wedding_planner', name: 'Wedding Planner' },
      { id: 'corporate_events', name: 'Corporate Events' },
      { id: 'party_hire', name: 'Party Hire & Equipment' },
      { id: 'marquee_hire', name: 'Marquee & Tent Hire' },
      { id: 'furniture_hire', name: 'Event Furniture Hire' },
      { id: 'av_hire', name: 'AV Equipment Hire' },
      { id: 'photographer', name: 'Photographer' },
      { id: 'videographer', name: 'Videographer' },
      { id: 'dj', name: 'DJ Services' },
      { id: 'live_band', name: 'Live Band / Musicians' },
      { id: 'entertainer', name: 'Entertainer / Performer' },
      { id: 'kids_entertainment', name: 'Kids Entertainment' },
      { id: 'face_painting', name: 'Face Painting' },
      { id: 'balloon_artist', name: 'Balloon Artist' },
      { id: 'magician', name: 'Magician' },
      { id: 'celebrant', name: 'Celebrant' },
      { id: 'florist_events', name: 'Event Florist' },
      { id: 'photo_booth', name: 'Photo Booth Hire' },
      { id: 'escape_room', name: 'Escape Room' },
      { id: 'laser_tag', name: 'Laser Tag' },
      { id: 'bowling', name: 'Bowling Alley' },
      { id: 'cinema', name: 'Cinema' },
      { id: 'theatre', name: 'Theatre' },
      { id: 'amusement_park', name: 'Amusement Park' },
      { id: 'zoo', name: 'Zoo / Wildlife Park' },
      { id: 'museum', name: 'Museum / Gallery' },
    ]
  },

  // -------------------------------------------------------------------------
  // AGRICULTURE & PRIMARY (ANZSIC Division A)
  // -------------------------------------------------------------------------
  {
    id: 'agriculture',
    name: 'Agriculture & Primary',
    icon: 'Leaf',
    color: '#16A34A',
    description: 'Farming, agriculture, and primary industries',
    anzsic_code: 'A',
    anzsic_division: 'Agriculture, Forestry and Fishing',
    subIndustries: [
      { id: 'cattle_farm', name: 'Cattle Farm' },
      { id: 'sheep_farm', name: 'Sheep Farm' },
      { id: 'dairy_farm', name: 'Dairy Farm' },
      { id: 'poultry_farm', name: 'Poultry Farm' },
      { id: 'pig_farm', name: 'Pig Farm' },
      { id: 'horse_farm', name: 'Horse Stud / Farm' },
      { id: 'grain_farm', name: 'Grain & Cropping' },
      { id: 'fruit_orchard', name: 'Fruit Orchard' },
      { id: 'vegetable_farm', name: 'Vegetable Farm' },
      { id: 'vineyard', name: 'Vineyard' },
      { id: 'nursery', name: 'Nursery & Plant Growing' },
      { id: 'hydroponics', name: 'Hydroponics' },
      { id: 'aquaculture', name: 'Aquaculture / Fish Farm' },
      { id: 'oyster_farm', name: 'Oyster / Shellfish Farm' },
      { id: 'beekeeping', name: 'Beekeeping / Apiary' },
      { id: 'cotton', name: 'Cotton Farm' },
      { id: 'sugar_cane', name: 'Sugar Cane' },
      { id: 'forestry', name: 'Forestry & Logging' },
      { id: 'fishing', name: 'Commercial Fishing' },
      { id: 'ag_services', name: 'Agricultural Services' },
      { id: 'ag_supplies', name: 'Agricultural Supplies' },
      { id: 'farm_machinery', name: 'Farm Machinery' },
    ]
  },

  // -------------------------------------------------------------------------
  // FINANCE & INSURANCE (ANZSIC Division K)
  // -------------------------------------------------------------------------
  {
    id: 'finance',
    name: 'Finance & Insurance',
    icon: 'PiggyBank',
    color: '#0D9488',
    description: 'Financial services and insurance',
    anzsic_code: 'K',
    anzsic_division: 'Financial and Insurance Services',
    subIndustries: [
      { id: 'bank', name: 'Bank' },
      { id: 'credit_union', name: 'Credit Union' },
      { id: 'finance_company', name: 'Finance Company' },
      { id: 'lending', name: 'Lending / Loans' },
      { id: 'factoring', name: 'Invoice Factoring' },
      { id: 'equipment_finance', name: 'Equipment Finance' },
      { id: 'wealth_management', name: 'Wealth Management' },
      { id: 'investment_firm', name: 'Investment Firm' },
      { id: 'superannuation', name: 'Superannuation Fund' },
      { id: 'stock_broker', name: 'Stock Broker' },
      { id: 'forex', name: 'Foreign Exchange' },
      { id: 'crypto', name: 'Cryptocurrency / Crypto Exchange' },
      { id: 'insurance_agency', name: 'Insurance Agency' },
      { id: 'insurance_company', name: 'Insurance Company' },
      { id: 'claims_management', name: 'Claims Management' },
      { id: 'debt_collection', name: 'Debt Collection' },
      { id: 'credit_repair', name: 'Credit Repair' },
    ]
  },

  // -------------------------------------------------------------------------
  // NON-PROFIT & COMMUNITY (ANZSIC Division S)
  // -------------------------------------------------------------------------
  {
    id: 'nonprofit',
    name: 'Non-Profit & Community',
    icon: 'Heart',
    color: '#E11D48',
    description: 'Charities, community organizations, and social enterprises',
    anzsic_code: 'S',
    anzsic_division: 'Other Services',
    subIndustries: [
      { id: 'charity', name: 'Charity' },
      { id: 'foundation', name: 'Foundation' },
      { id: 'ngo', name: 'NGO' },
      { id: 'social_enterprise', name: 'Social Enterprise' },
      { id: 'community_org', name: 'Community Organisation' },
      { id: 'religious_org', name: 'Religious Organisation' },
      { id: 'church', name: 'Church' },
      { id: 'sporting_club', name: 'Sporting Club' },
      { id: 'rsl', name: 'RSL / Veterans Club' },
      { id: 'bowling_club', name: 'Bowling Club' },
      { id: 'golf_club_nonprofit', name: 'Golf Club (Non-Profit)' },
      { id: 'surf_club', name: 'Surf Life Saving Club' },
      { id: 'scouts_guides', name: 'Scouts / Guides' },
      { id: 'rotary', name: 'Rotary / Lions / Service Club' },
      { id: 'advocacy', name: 'Advocacy Group' },
      { id: 'environmental', name: 'Environmental Organisation' },
      { id: 'animal_welfare', name: 'Animal Welfare Organisation' },
      { id: 'homeless_services', name: 'Homeless Services' },
      { id: 'disability_services', name: 'Disability Services' },
      { id: 'youth_services', name: 'Youth Services' },
      { id: 'aged_services', name: 'Aged Services (Non-Profit)' },
    ]
  },

  // -------------------------------------------------------------------------
  // MEDIA & CREATIVE (ANZSIC Division J)
  // -------------------------------------------------------------------------
  {
    id: 'media',
    name: 'Media & Creative',
    icon: 'Camera',
    color: '#7C3AED',
    description: 'Media, publishing, and creative industries',
    anzsic_code: 'J',
    anzsic_division: 'Information Media and Telecommunications',
    subIndustries: [
      { id: 'advertising', name: 'Advertising Agency' },
      { id: 'media_agency', name: 'Media Agency' },
      { id: 'production_company', name: 'Production Company' },
      { id: 'film_production', name: 'Film Production' },
      { id: 'tv_production', name: 'TV Production' },
      { id: 'animation_studio', name: 'Animation Studio' },
      { id: 'recording_studio', name: 'Recording Studio' },
      { id: 'podcast_production', name: 'Podcast Production' },
      { id: 'radio_station', name: 'Radio Station' },
      { id: 'newspaper', name: 'Newspaper / Magazine' },
      { id: 'publisher', name: 'Publisher' },
      { id: 'graphic_design', name: 'Graphic Design Studio' },
      { id: 'branding_agency', name: 'Branding Agency' },
      { id: 'photography_studio', name: 'Photography Studio' },
      { id: 'modelling_agency', name: 'Modelling Agency' },
      { id: 'talent_agency', name: 'Talent Agency' },
      { id: 'influencer', name: 'Influencer / Content Creator' },
      { id: 'streaming', name: 'Streaming / OTT Platform' },
    ]
  },

  // -------------------------------------------------------------------------
  // GOVERNMENT & PUBLIC SECTOR (ANZSIC Division O)
  // -------------------------------------------------------------------------
  {
    id: 'government',
    name: 'Government & Public Sector',
    icon: 'Building',
    color: '#1E3A8A',
    description: 'Government agencies and public sector organisations',
    anzsic_code: 'O',
    anzsic_division: 'Public Administration and Safety',
    subIndustries: [
      { id: 'local_council', name: 'Local Council' },
      { id: 'state_govt', name: 'State Government Agency' },
      { id: 'federal_govt', name: 'Federal Government Agency' },
      { id: 'public_hospital', name: 'Public Hospital' },
      { id: 'public_school', name: 'Public School' },
      { id: 'tafe', name: 'TAFE' },
      { id: 'university', name: 'University' },
      { id: 'library', name: 'Library' },
      { id: 'emergency_services', name: 'Emergency Services' },
      { id: 'defence', name: 'Defence' },
      { id: 'utilities', name: 'Utilities (Water, Power)' },
    ]
  },

  // -------------------------------------------------------------------------
  // MARINE & BOATING (ANZSIC Division I/R)
  // -------------------------------------------------------------------------
  {
    id: 'marine',
    name: 'Marine & Boating',
    icon: 'Anchor',
    color: '#0284C7',
    description: 'Marine services and boating industry',
    anzsic_code: 'I',
    anzsic_division: 'Transport, Postal and Warehousing',
    subIndustries: [
      { id: 'marina', name: 'Marina' },
      { id: 'boat_charter', name: 'Boat Charter' },
      { id: 'yacht_club', name: 'Yacht Club' },
      { id: 'boat_repair', name: 'Boat Repair & Service' },
      { id: 'boat_detailing', name: 'Boat Detailing' },
      { id: 'boat_storage', name: 'Boat Storage' },
      { id: 'marine_electronics', name: 'Marine Electronics' },
      { id: 'boat_broker', name: 'Boat Broker' },
      { id: 'sailing_school', name: 'Sailing School' },
      { id: 'diving', name: 'Diving Services' },
      { id: 'water_sports', name: 'Water Sports' },
    ]
  },

  // -------------------------------------------------------------------------
  // OTHER / CUSTOM (ANZSIC Division Various)
  // -------------------------------------------------------------------------
  {
    id: 'other',
    name: 'Other Industry',
    icon: 'Building',
    color: '#64748b',
    description: 'Other industries not listed above',
    anzsic_code: 'S',
    anzsic_division: 'Other Services',
    subIndustries: [
      { id: 'funeral_services', name: 'Funeral Services' },
      { id: 'cemetery', name: 'Cemetery / Memorial Park' },
      { id: 'security_services', name: 'Security Services' },
      { id: 'traffic_control', name: 'Traffic Control' },
      { id: 'mining', name: 'Mining' },
      { id: 'oil_gas', name: 'Oil & Gas' },
      { id: 'renewable_energy', name: 'Renewable Energy' },
      { id: 'utilities_contractor', name: 'Utilities Contractor' },
      { id: 'franchise', name: 'Franchise (Multi-Industry)' },
      { id: 'holding_company', name: 'Holding Company' },
      { id: 'custom', name: 'Other - Specify Below' },
    ]
  },
];

// =============================================================================
// INDUSTRY TERMINOLOGY - Language specific to each industry
// =============================================================================
export const INDUSTRY_TERMINOLOGY = {
  // Healthcare terminology - use "patients" not "customers"
  healthcare: {
    customer: 'patient',
    customers: 'patients',
    appointment: 'appointment',
    appointments: 'appointments',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'consultation',
    sales: 'consultations',
    order: 'referral',
    orders: 'referrals',
    product: 'treatment',
    products: 'treatments',
    service: 'service',
    services: 'services',
    location: 'clinic',
    locations: 'clinics',
    staff: 'practitioners',
    revenue: 'billings',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'checkout',
    cart: 'treatment plan',
    loyalty: 'recall',
    review: 'testimonial',
    reviews: 'testimonials',
    aiContext: 'This is a healthcare business. Use medical terminology. Refer to customers as "patients" and understand healthcare-specific KPIs like patient retention, average consultation value, and recall rates.',
  },
  
  // Veterinary - use "patients" for animals, "clients" for owners
  veterinary: {
    customer: 'client',
    customers: 'clients',
    patient: 'patient', // the animal
    patients: 'patients',
    appointment: 'appointment',
    appointments: 'appointments',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'consultation',
    sales: 'consultations',
    product: 'treatment',
    products: 'treatments',
    service: 'service',
    services: 'services',
    location: 'clinic',
    locations: 'clinics',
    staff: 'veterinary team',
    revenue: 'billings',
    aiContext: 'This is a veterinary business. Clients are pet owners, patients are the animals. Track metrics like average patient value, vaccination reminders, and client retention.',
  },
  
  // Hospitality - use "guests" not "customers"
  hospitality: {
    customer: 'guest',
    customers: 'guests',
    appointment: 'reservation',
    appointments: 'reservations',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'cover',
    sales: 'covers',
    order: 'order',
    orders: 'orders',
    product: 'dish',
    products: 'menu items',
    service: 'service',
    services: 'services',
    location: 'venue',
    locations: 'venues',
    staff: 'team members',
    revenue: 'revenue',
    invoice: 'bill',
    invoices: 'bills',
    checkout: 'payment',
    cart: 'order',
    loyalty: 'loyalty program',
    review: 'review',
    reviews: 'reviews',
    aiContext: 'This is a hospitality business. Use restaurant/cafe/bar terminology. Track covers, average spend per head, table turnover, peak hours, and staff efficiency.',
  },
  
  // Accommodation - use "guests" and hotel terminology
  accommodation: {
    customer: 'guest',
    customers: 'guests',
    appointment: 'reservation',
    appointments: 'reservations',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'room night',
    sales: 'room nights',
    order: 'booking',
    orders: 'bookings',
    product: 'room',
    products: 'rooms',
    service: 'amenity',
    services: 'amenities',
    location: 'property',
    locations: 'properties',
    staff: 'staff',
    revenue: 'revenue',
    invoice: 'folio',
    invoices: 'folios',
    aiContext: 'This is an accommodation business. Track occupancy rates, RevPAR, ADR, and booking sources. Understand seasonality and length of stay metrics.',
  },
  
  // Retail - standard terms but specific metrics
  retail: {
    customer: 'customer',
    customers: 'customers',
    appointment: 'appointment',
    appointments: 'appointments',
    booking: 'order',
    bookings: 'orders',
    sale: 'sale',
    sales: 'sales',
    order: 'order',
    orders: 'orders',
    product: 'product',
    products: 'products',
    service: 'service',
    services: 'services',
    location: 'store',
    locations: 'stores',
    staff: 'team members',
    revenue: 'revenue',
    invoice: 'receipt',
    invoices: 'receipts',
    checkout: 'checkout',
    cart: 'cart',
    loyalty: 'loyalty program',
    review: 'review',
    reviews: 'reviews',
    aiContext: 'This is a retail business. Track metrics like basket size, conversion rate, foot traffic, inventory turnover, and sell-through rate.',
  },
  
  // Beauty - use "clients" and salon terminology
  beauty: {
    customer: 'client',
    customers: 'clients',
    appointment: 'appointment',
    appointments: 'appointments',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'service',
    sales: 'services',
    order: 'booking',
    orders: 'bookings',
    product: 'treatment',
    products: 'treatments',
    service: 'service',
    services: 'services',
    location: 'salon',
    locations: 'salons',
    staff: 'stylists',
    revenue: 'revenue',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'checkout',
    cart: 'services',
    loyalty: 'loyalty program',
    review: 'review',
    reviews: 'reviews',
    aiContext: 'This is a beauty/salon business. Track client retention, rebooking rate, average service value, stylist productivity, and retail attachment rate.',
  },
  
  // Fitness - use "members" 
  fitness: {
    customer: 'member',
    customers: 'members',
    appointment: 'class',
    appointments: 'classes',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'membership',
    sales: 'memberships',
    order: 'sign-up',
    orders: 'sign-ups',
    product: 'membership',
    products: 'memberships',
    service: 'class',
    services: 'classes',
    location: 'facility',
    locations: 'facilities',
    staff: 'trainers',
    revenue: 'revenue',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'sign-up',
    cart: 'membership',
    loyalty: 'membership',
    review: 'review',
    reviews: 'reviews',
    aiContext: 'This is a fitness business. Track member retention, class attendance, PT session uptake, membership churn, and average revenue per member.',
  },
  
  // Professional Services - use "clients"
  professional: {
    customer: 'client',
    customers: 'clients',
    appointment: 'meeting',
    appointments: 'meetings',
    booking: 'engagement',
    bookings: 'engagements',
    sale: 'engagement',
    sales: 'engagements',
    order: 'matter',
    orders: 'matters',
    product: 'service',
    products: 'services',
    service: 'service',
    services: 'services',
    location: 'office',
    locations: 'offices',
    staff: 'team members',
    revenue: 'fees',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'payment',
    cart: 'engagement',
    loyalty: 'retainer',
    review: 'testimonial',
    reviews: 'testimonials',
    aiContext: 'This is a professional services firm. Track billable hours, utilisation rate, client retention, average matter value, and WIP.',
  },
  
  // Construction & Trades - use "clients" and job terminology
  construction: {
    customer: 'client',
    customers: 'clients',
    appointment: 'site visit',
    appointments: 'site visits',
    booking: 'job',
    bookings: 'jobs',
    sale: 'job',
    sales: 'jobs',
    order: 'job',
    orders: 'jobs',
    product: 'service',
    products: 'services',
    service: 'service',
    services: 'services',
    location: 'site',
    locations: 'sites',
    staff: 'tradies',
    revenue: 'revenue',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'payment',
    cart: 'quote',
    loyalty: 'preferred customer',
    review: 'review',
    reviews: 'reviews',
    aiContext: 'This is a construction/trades business. Track job profitability, quote conversion rate, job completion time, and callback rate.',
  },
  
  // Education - use "students"
  education: {
    customer: 'student',
    customers: 'students',
    appointment: 'class',
    appointments: 'classes',
    booking: 'enrolment',
    bookings: 'enrolments',
    sale: 'enrolment',
    sales: 'enrolments',
    order: 'enrolment',
    orders: 'enrolments',
    product: 'course',
    products: 'courses',
    service: 'class',
    services: 'classes',
    location: 'campus',
    locations: 'campuses',
    staff: 'educators',
    revenue: 'fees',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'enrolment',
    cart: 'enrolment',
    loyalty: 'alumni',
    review: 'review',
    reviews: 'reviews',
    aiContext: 'This is an education business. Track enrolment rates, student retention, attendance, course completion, and student satisfaction.',
  },
  
  // Childcare specific
  childcare: {
    customer: 'family',
    customers: 'families',
    child: 'child',
    children: 'children',
    appointment: 'session',
    appointments: 'sessions',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'enrolment',
    sales: 'enrolments',
    product: 'program',
    products: 'programs',
    service: 'care',
    services: 'care services',
    location: 'centre',
    locations: 'centres',
    staff: 'educators',
    revenue: 'fees',
    aiContext: 'This is a childcare business. Track occupancy, CCS claims, educator-to-child ratios, and family satisfaction.',
  },
  
  // Wholesale/B2B - use "accounts" or "dealers"
  wholesale: {
    customer: 'account',
    customers: 'accounts',
    appointment: 'meeting',
    appointments: 'meetings',
    booking: 'order',
    bookings: 'orders',
    sale: 'order',
    sales: 'orders',
    order: 'order',
    orders: 'orders',
    product: 'product',
    products: 'products',
    service: 'service',
    services: 'services',
    location: 'warehouse',
    locations: 'warehouses',
    staff: 'sales reps',
    revenue: 'revenue',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'order',
    cart: 'order',
    loyalty: 'volume discount',
    review: 'feedback',
    reviews: 'feedback',
    aiContext: 'This is a wholesale/distribution business. Track order value, order frequency, account growth, and margin per account.',
  },
  
  // Technology/SaaS - use "users" or "customers"
  technology: {
    customer: 'customer',
    customers: 'customers',
    user: 'user',
    users: 'users',
    appointment: 'demo',
    appointments: 'demos',
    booking: 'subscription',
    bookings: 'subscriptions',
    sale: 'subscription',
    sales: 'subscriptions',
    order: 'subscription',
    orders: 'subscriptions',
    product: 'plan',
    products: 'plans',
    service: 'feature',
    services: 'features',
    location: 'region',
    locations: 'regions',
    staff: 'team',
    revenue: 'MRR',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'sign-up',
    cart: 'plan',
    loyalty: 'subscription',
    review: 'review',
    reviews: 'reviews',
    aiContext: 'This is a technology/SaaS business. Track MRR, ARR, churn rate, CAC, LTV, and user engagement metrics.',
  },
  
  // Manufacturing - use "customers" and production terminology
  manufacturing: {
    customer: 'customer',
    customers: 'customers',
    appointment: 'meeting',
    appointments: 'meetings',
    booking: 'order',
    bookings: 'orders',
    sale: 'order',
    sales: 'orders',
    order: 'order',
    orders: 'orders',
    product: 'product',
    products: 'products',
    service: 'service',
    services: 'services',
    location: 'plant',
    locations: 'plants',
    staff: 'workers',
    revenue: 'revenue',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'order',
    cart: 'order',
    aiContext: 'This is a manufacturing business. Track production efficiency, OEE, defect rate, order fulfillment, and capacity utilisation.',
  },
  
  // Transport/Logistics
  transport: {
    customer: 'customer',
    customers: 'customers',
    appointment: 'pickup',
    appointments: 'pickups',
    booking: 'shipment',
    bookings: 'shipments',
    sale: 'delivery',
    sales: 'deliveries',
    order: 'consignment',
    orders: 'consignments',
    product: 'service',
    products: 'services',
    service: 'service',
    services: 'services',
    location: 'depot',
    locations: 'depots',
    staff: 'drivers',
    revenue: 'revenue',
    invoice: 'invoice',
    invoices: 'invoices',
    aiContext: 'This is a transport/logistics business. Track on-time delivery, cost per delivery, fleet utilisation, and customer satisfaction.',
  },
  
  // Events - use "clients"
  events: {
    customer: 'client',
    customers: 'clients',
    appointment: 'consultation',
    appointments: 'consultations',
    booking: 'event',
    bookings: 'events',
    sale: 'event',
    sales: 'events',
    order: 'booking',
    orders: 'bookings',
    product: 'package',
    products: 'packages',
    service: 'service',
    services: 'services',
    location: 'venue',
    locations: 'venues',
    staff: 'team',
    revenue: 'revenue',
    invoice: 'invoice',
    invoices: 'invoices',
    aiContext: 'This is an events business. Track bookings, average event value, lead conversion, and client satisfaction.',
  },
  
  // Non-profit - use "donors" or "supporters"
  nonprofit: {
    customer: 'supporter',
    customers: 'supporters',
    donor: 'donor',
    donors: 'donors',
    appointment: 'meeting',
    appointments: 'meetings',
    booking: 'donation',
    bookings: 'donations',
    sale: 'donation',
    sales: 'donations',
    order: 'gift',
    orders: 'gifts',
    product: 'program',
    products: 'programs',
    service: 'service',
    services: 'services',
    location: 'chapter',
    locations: 'chapters',
    staff: 'team',
    revenue: 'donations',
    invoice: 'receipt',
    invoices: 'receipts',
    aiContext: 'This is a non-profit organisation. Track donation growth, donor retention, cost per dollar raised, and program outcomes.',
  },
  
  // Real Estate - use "vendors" and "buyers"
  real_estate: {
    customer: 'client',
    customers: 'clients',
    vendor: 'vendor',
    vendors: 'vendors',
    buyer: 'buyer',
    buyers: 'buyers',
    appointment: 'inspection',
    appointments: 'inspections',
    booking: 'listing',
    bookings: 'listings',
    sale: 'sale',
    sales: 'sales',
    order: 'listing',
    orders: 'listings',
    product: 'property',
    products: 'properties',
    service: 'service',
    services: 'services',
    location: 'office',
    locations: 'offices',
    staff: 'agents',
    revenue: 'commission',
    invoice: 'invoice',
    invoices: 'invoices',
    aiContext: 'This is a real estate business. Track listings, days on market, average commission, appraisals, and market share.',
  },
  
  // Agriculture
  agriculture: {
    customer: 'buyer',
    customers: 'buyers',
    appointment: 'visit',
    appointments: 'visits',
    booking: 'order',
    bookings: 'orders',
    sale: 'harvest',
    sales: 'harvests',
    order: 'order',
    orders: 'orders',
    product: 'produce',
    products: 'produce',
    service: 'service',
    services: 'services',
    location: 'farm',
    locations: 'farms',
    staff: 'farmhands',
    revenue: 'revenue',
    aiContext: 'This is an agricultural business. Track yield, cost per hectare, commodity prices, and seasonal patterns.',
  },
  
  // Home Services
  home_services: {
    customer: 'customer',
    customers: 'customers',
    appointment: 'appointment',
    appointments: 'appointments',
    booking: 'job',
    bookings: 'jobs',
    sale: 'job',
    sales: 'jobs',
    order: 'job',
    orders: 'jobs',
    product: 'service',
    products: 'services',
    service: 'service',
    services: 'services',
    location: 'service area',
    locations: 'service areas',
    staff: 'technicians',
    revenue: 'revenue',
    aiContext: 'This is a home services business. Track jobs per day, average job value, repeat customers, and customer satisfaction.',
  },
  
  // Automotive
  automotive: {
    customer: 'customer',
    customers: 'customers',
    appointment: 'booking',
    appointments: 'bookings',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'sale',
    sales: 'sales',
    order: 'repair order',
    orders: 'repair orders',
    product: 'vehicle',
    products: 'vehicles',
    service: 'service',
    services: 'services',
    location: 'dealership',
    locations: 'dealerships',
    staff: 'team',
    revenue: 'revenue',
    aiContext: 'This is an automotive business. Track vehicle sales, service revenue, average repair order, and customer retention.',
  },

  // Marine
  marine: {
    customer: 'boat owner',
    customers: 'boat owners',
    appointment: 'booking',
    appointments: 'bookings',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'sale',
    sales: 'sales',
    order: 'job',
    orders: 'jobs',
    product: 'vessel',
    products: 'vessels',
    service: 'service',
    services: 'services',
    location: 'marina',
    locations: 'marinas',
    staff: 'team',
    revenue: 'revenue',
    aiContext: 'This is a marine business. Track berth occupancy, service revenue, and seasonal patterns.',
  },

  // Finance & Insurance
  finance: {
    customer: 'client',
    customers: 'clients',
    appointment: 'meeting',
    appointments: 'meetings',
    booking: 'application',
    bookings: 'applications',
    sale: 'settlement',
    sales: 'settlements',
    order: 'application',
    orders: 'applications',
    product: 'product',
    products: 'products',
    service: 'service',
    services: 'services',
    location: 'branch',
    locations: 'branches',
    staff: 'advisers',
    revenue: 'revenue',
    aiContext: 'This is a financial services business. Track loan settlements, conversion rates, and client AUM.',
  },

  // Media & Creative
  media: {
    customer: 'client',
    customers: 'clients',
    appointment: 'meeting',
    appointments: 'meetings',
    booking: 'project',
    bookings: 'projects',
    sale: 'project',
    sales: 'projects',
    order: 'brief',
    orders: 'briefs',
    product: 'content',
    products: 'content',
    service: 'service',
    services: 'services',
    location: 'studio',
    locations: 'studios',
    staff: 'creatives',
    revenue: 'revenue',
    aiContext: 'This is a media/creative business. Track project value, client retention, and creative output.',
  },

  // Government
  government: {
    customer: 'citizen',
    customers: 'citizens',
    constituent: 'constituent',
    constituents: 'constituents',
    appointment: 'appointment',
    appointments: 'appointments',
    booking: 'service request',
    bookings: 'service requests',
    sale: 'service',
    sales: 'services',
    product: 'service',
    products: 'services',
    service: 'service',
    services: 'services',
    location: 'office',
    locations: 'offices',
    staff: 'staff',
    revenue: 'budget',
    aiContext: 'This is a government/public sector organisation. Track service delivery, citizen satisfaction, and compliance.',
  },

  // Default fallback
  default: {
    customer: 'customer',
    customers: 'customers',
    appointment: 'appointment',
    appointments: 'appointments',
    booking: 'booking',
    bookings: 'bookings',
    sale: 'sale',
    sales: 'sales',
    order: 'order',
    orders: 'orders',
    product: 'product',
    products: 'products',
    service: 'service',
    services: 'services',
    location: 'location',
    locations: 'locations',
    staff: 'staff',
    revenue: 'revenue',
    invoice: 'invoice',
    invoices: 'invoices',
    checkout: 'checkout',
    cart: 'cart',
    loyalty: 'loyalty',
    review: 'review',
    reviews: 'reviews',
    aiContext: 'This is a general business. Use standard business terminology and track common KPIs.',
  },
};

// =============================================================================
// INDUSTRY API MAPPINGS - Recommended integrations per industry
// =============================================================================
export const INDUSTRY_API_MAPPINGS = {
  healthcare: {
    primaryApis: ['cliniko', 'hotdoc', 'medicare_australia', 'nookal', 'hicaps', 'pracsoft', 'best_practice', 'tyro'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['tyro', 'square', 'stripe', 'hicaps'],
    marketing: ['mailchimp', 'google_business', 'facebook'],
    hr: ['deputy', 'tanda', 'employment_hero'],
    analytics: ['google_analytics'],
    description: 'Healthcare businesses benefit from practice management integrations for patient records, Medicare claiming, and health fund processing.',
  },
  
  veterinary: {
    primaryApis: ['ezyvet', 'rx_works', 'vision_vpm', 'smart_flow', 'idexx'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['tyro', 'square', 'stripe'],
    marketing: ['mailchimp', 'sms_broadcast'],
    hr: ['deputy', 'tanda'],
    analytics: ['google_analytics'],
    description: 'Veterinary practices need practice management systems with patient records, treatment history, and reminder systems.',
  },
  
  hospitality: {
    primaryApis: ['square', 'toast', 'kounta', 'impos', 'revel', 'touchbistro'],
    reservations: ['opentable', 'resy', 'sevenrooms', 'now_book_it', 'quandoo'],
    delivery: ['uber_eats', 'doordash', 'menulog', 'deliveroo'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['tyro', 'square', 'stripe', 'afterpay'],
    hr: ['deputy', 'tanda', 'humanforce'],
    inventory: ['marketman', 'yellowfin'],
    analytics: ['google_analytics', 'yumpingo'],
    description: 'Hospitality businesses need POS integration, reservation systems, and delivery platform connections.',
  },
  
  accommodation: {
    primaryApis: ['protel', 'opera', 'cloudbeds', 'little_hotelier', 'roommaster', 'stayntouch'],
    distribution: ['booking_com', 'expedia', 'airbnb', 'agoda', 'tripadvisor'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square', 'tyro'],
    marketing: ['mailchimp', 'google_business'],
    hr: ['deputy', 'tanda'],
    analytics: ['google_analytics', 'revinate'],
    description: 'Accommodation businesses need property management systems and channel managers for bookings.',
  },
  
  retail: {
    primaryApis: ['shopify', 'vend', 'square', 'cin7', 'dear'],
    ecommerce: ['shopify', 'woocommerce', 'bigcommerce', 'magento'],
    marketplaces: ['amazon', 'ebay', 'catch', 'kogan'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square', 'tyro', 'afterpay', 'zip'],
    shipping: ['shipstation', 'sendle', 'auspost', 'startrack'],
    marketing: ['mailchimp', 'klaviyo', 'google_ads', 'facebook_ads'],
    hr: ['deputy', 'tanda'],
    analytics: ['google_analytics', 'hotjar'],
    description: 'Retail businesses need POS, inventory management, and e-commerce platform integration.',
  },
  
  beauty: {
    primaryApis: ['fresha', 'shortcuts', 'timely', 'phorest', 'mindbody', 'vagaro', 'square_appointments'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square', 'tyro'],
    marketing: ['mailchimp', 'instagram', 'facebook'],
    hr: ['deputy', 'tanda'],
    analytics: ['google_analytics', 'google_business'],
    description: 'Beauty businesses need appointment booking, client management, and automated reminders.',
  },
  
  fitness: {
    primaryApis: ['mindbody', 'wodify', 'push_press', 'gym_master', 'perfect_gym', 'glofox'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square', 'ezidebit', 'debitsuccess'],
    marketing: ['mailchimp', 'activecampaign'],
    hr: ['deputy', 'tanda'],
    analytics: ['google_analytics'],
    description: 'Fitness businesses need membership management, class booking, and direct debit integration.',
  },
  
  construction: {
    primaryApis: ['simpro', 'servicem8', 'tradify', 'fergus', 'buildxact', 'procore', 'buildertrend'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square', 'tyro'],
    hr: ['deputy', 'tanda', 'employment_hero'],
    analytics: ['google_analytics'],
    description: 'Construction and trades need job management, quoting, and scheduling integration.',
  },
  
  professional: {
    primaryApis: ['practice_ignition', 'karbon', 'clio', 'actionstep', 'leap', 'xplan'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square'],
    marketing: ['hubspot', 'mailchimp'],
    hr: ['employment_hero', 'deputy'],
    analytics: ['google_analytics'],
    description: 'Professional services need practice management, time tracking, and document management.',
  },
  
  technology: {
    primaryApis: ['stripe', 'chargebee', 'paddle', 'hubspot', 'salesforce', 'intercom', 'zendesk'],
    analytics: ['google_analytics', 'mixpanel', 'amplitude', 'segment', 'heap'],
    accounting: ['xero', 'quickbooks'],
    payments: ['stripe', 'paddle', 'braintree'],
    marketing: ['hubspot', 'mailchimp', 'activecampaign'],
    hr: ['employment_hero', 'bamboohr', 'gusto'],
    description: 'Tech businesses need subscription billing, analytics, and customer success tools.',
  },
  
  education: {
    primaryApis: ['qikkids', 'harmony', 'xplor', 'kinderloop', 'storypark', 'compass', 'tass'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'ezidebit'],
    marketing: ['mailchimp'],
    hr: ['deputy', 'tanda'],
    analytics: ['google_analytics'],
    description: 'Education businesses need student/enrolment management and parent communication.',
  },
  
  wholesale: {
    primaryApis: ['cin7', 'dear', 'unleashed', 'tradegecko', 'netsuite', 'sap'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square'],
    shipping: ['shipstation', 'startrack', 'toll'],
    analytics: ['google_analytics'],
    description: 'Wholesale businesses need inventory management, order processing, and B2B portals.',
  },
  
  manufacturing: {
    primaryApis: ['fishbowl', 'netsuite', 'sap', 'dear', 'cin7', 'katana'],
    accounting: ['xero', 'myob', 'quickbooks', 'netsuite'],
    payments: ['stripe'],
    hr: ['deputy', 'employment_hero'],
    analytics: ['google_analytics'],
    description: 'Manufacturing businesses need production planning, inventory, and quality tracking.',
  },
  
  transport: {
    primaryApis: ['cartoncloud', 'machship', 'startrack', 'toll', 'dhl', 'fedex'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe'],
    hr: ['deputy', 'tanda'],
    analytics: ['google_analytics'],
    description: 'Transport businesses need route optimisation, consignment tracking, and fleet management.',
  },
  
  events: {
    primaryApis: ['eventbrite', 'humanitix', 'trybooking', 'ticketek', 'honeybook', '17hats'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square'],
    marketing: ['mailchimp'],
    analytics: ['google_analytics'],
    description: 'Events businesses need ticketing, venue management, and client communication.',
  },
  
  agriculture: {
    primaryApis: ['agworld', 'figured', 'farm_at_hand', 'agrivi'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe'],
    analytics: ['google_analytics'],
    description: 'Agriculture businesses need farm management, crop tracking, and commodity pricing.',
  },
  
  nonprofit: {
    primaryApis: ['salesforce_npsp', 'bloomerang', 'little_green_light', 'givewp', 'chargebee'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'paypal'],
    marketing: ['mailchimp', 'activecampaign'],
    analytics: ['google_analytics'],
    description: 'Non-profits need donor management, grant tracking, and fundraising tools.',
  },
  
  automotive: {
    primaryApis: ['automate', 'cdk', 'reynolds', 'dealer_solutions', 'workshop_software', 'costar'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'tyro', 'afterpay'],
    marketing: ['mailchimp', 'google_ads'],
    hr: ['deputy', 'tanda'],
    analytics: ['google_analytics'],
    description: 'Automotive businesses need DMS integration, workshop management, and parts catalogues.',
  },
  
  home_services: {
    primaryApis: ['servicem8', 'jobber', 'housecall_pro', 'servicetitan', 'simpro'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square'],
    marketing: ['mailchimp', 'google_business'],
    hr: ['deputy'],
    analytics: ['google_analytics'],
    description: 'Home services need job scheduling, dispatch, and customer communication.',
  },
  
  finance: {
    primaryApis: ['salesforce', 'xplan', 'midwinter', 'class_super', 'bt_panorama'],
    accounting: ['xero', 'myob'],
    payments: ['stripe'],
    marketing: ['hubspot', 'mailchimp'],
    analytics: ['google_analytics'],
    description: 'Financial services need client management, portfolio tracking, and compliance tools.',
  },
  
  marine: {
    primaryApis: ['marina_master', 'dockmaster', 'havenstar'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square'],
    analytics: ['google_analytics'],
    description: 'Marine businesses need berth management, customer records, and service scheduling.',
  },
  
  media: {
    primaryApis: ['monday', 'asana', 'basecamp', 'wrike', 'harvest'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe'],
    marketing: ['hubspot', 'mailchimp'],
    analytics: ['google_analytics'],
    description: 'Media businesses need project management, time tracking, and client collaboration.',
  },
  
  government: {
    primaryApis: ['salesforce_gov', 'dynamics', 'servicenow'],
    accounting: ['technology_one', 'sap'],
    analytics: ['google_analytics'],
    description: 'Government organisations need citizen services, compliance tracking, and reporting.',
  },
  
  default: {
    primaryApis: ['stripe', 'square', 'google_analytics'],
    accounting: ['xero', 'myob', 'quickbooks'],
    payments: ['stripe', 'square', 'paypal'],
    marketing: ['mailchimp', 'hubspot', 'google_ads'],
    hr: ['deputy', 'tanda', 'employment_hero'],
    analytics: ['google_analytics', 'hotjar'],
    description: 'Standard business integrations for accounting, payments, and marketing.',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get industry category by sub-industry ID
 */
export const getCategoryBySubIndustry = (subIndustryId) => {
  return INDUSTRY_CATEGORIES.find(cat => 
    cat.subIndustries.some(sub => sub.id === subIndustryId)
  );
};

/**
 * Get terminology for an industry (checks sub-industry first, then category)
 */
export const getIndustryTerminology = (industryId) => {
  // Check if it's a direct category match
  if (INDUSTRY_TERMINOLOGY[industryId]) {
    return INDUSTRY_TERMINOLOGY[industryId];
  }
  
  // Find the parent category
  const category = getCategoryBySubIndustry(industryId);
  if (category && INDUSTRY_TERMINOLOGY[category.id]) {
    return INDUSTRY_TERMINOLOGY[category.id];
  }
  
  // Special case mappings for specific sub-industries
  const specialMappings = {
    // Childcare has its own terminology
    'childcare': 'childcare',
    'preschool': 'childcare',
    'oosh': 'childcare',
    'vacation_care': 'childcare',
    
    // Real estate specific
    'real_estate': 'real_estate',
    'property_management': 'real_estate',
    'buyers_agent': 'real_estate',
  };
  
  if (specialMappings[industryId]) {
    return INDUSTRY_TERMINOLOGY[specialMappings[industryId]] || INDUSTRY_TERMINOLOGY.default;
  }
  
  return INDUSTRY_TERMINOLOGY.default;
};

/**
 * Get API mappings for an industry
 */
export const getIndustryApiMappings = (industryId) => {
  // Check if it's a direct category match
  if (INDUSTRY_API_MAPPINGS[industryId]) {
    return INDUSTRY_API_MAPPINGS[industryId];
  }
  
  // Find the parent category
  const category = getCategoryBySubIndustry(industryId);
  if (category && INDUSTRY_API_MAPPINGS[category.id]) {
    return INDUSTRY_API_MAPPINGS[category.id];
  }
  
  return INDUSTRY_API_MAPPINGS.default;
};

/**
 * Translate a term for a specific industry
 * Example: translateTerm('customers', 'dental') => 'patients'
 */
export const translateTerm = (term, industryId) => {
  const terminology = getIndustryTerminology(industryId);
  return terminology[term] || INDUSTRY_TERMINOLOGY.default[term] || term;
};

/**
 * Get AI context prompt for an industry
 */
export const getAiContext = (industryId) => {
  const terminology = getIndustryTerminology(industryId);
  return terminology.aiContext || INDUSTRY_TERMINOLOGY.default.aiContext;
};

/**
 * Flatten all industries for backwards compatibility
 */
export const INDUSTRIES = INDUSTRY_CATEGORIES.flatMap(cat => 
  cat.subIndustries.map(sub => ({
    value: sub.id,
    label: sub.name,
    category: cat.id,
    categoryName: cat.name,
    color: cat.color
  }))
);

/**
 * Get total count of industries
 */
export const getIndustryCount = () => {
  return INDUSTRY_CATEGORIES.reduce((total, cat) => total + cat.subIndustries.length, 0);
};

// Export everything
export default {
  INDUSTRY_CATEGORIES,
  INDUSTRY_TERMINOLOGY,
  INDUSTRY_API_MAPPINGS,
  INDUSTRIES,
  getCategoryBySubIndustry,
  getIndustryTerminology,
  getIndustryApiMappings,
  translateTerm,
  getAiContext,
  getIndustryCount,
};
